import config from '@/config'
import {
  capitalize,
  displayNameOrNull,
  formatCurrency,
  formatUsDate,
  getQuotePdfFilename,
  toIntlLocale,
} from '@repo/utils'
import { useTenantNavigation } from '@/hooks/useTenantNavigation'
import { versionStatusKey } from '@/lib/quoteStatus'
import { getApi } from '@/stores/api'
import { getSessionToken } from '@/stores/session'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
} from '@repo/ui/components'
import { AUTHORIZATION_HEADER_PREFIX } from '@repo/constants'
import { PdfViewer } from '@repo/pdf/viewer'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  CircleDotIcon,
  Loader2,
  UserIcon,
  XCircleIcon,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

export const Route = createFileRoute('/_contractor/$tenantSlug/quotes/$quoteNumber')({
  component: QuoteDetailPage,
})

function getStatusDotClass(status: string | null) {
  switch (status) {
    case 'sent':
      return 'text-amber-500'
    case 'approved':
      return 'text-green-500'
    case 'rejected':
      return 'text-red-500'
    default:
      return 'text-gray-400'
  }
}

function QuoteDetailPage() {
  const { t, i18n } = useTranslation(['quotes', 'common'])
  const { quoteNumber, tenantSlug } = Route.useParams()
  const api = getApi()
  const queryClient = useQueryClient()
  const { navigate } = useTenantNavigation()

  // PET-2999: the slug names the OFFICE that owns this quote. Quote numbers are
  // unique per office, not globally, so without it a merged list holding two
  // quotes numbered the same resolves to whichever is newer.
  const officeQuery = { office: tenantSlug }

  const { data: quote, isLoading } = useQuery({
    queryKey: ['contractor-quote', tenantSlug, quoteNumber],
    queryFn: async () => {
      const res = await api.contractor.quotes[':id'].$get({
        param: { id: quoteNumber },
        query: officeQuery,
      })
      if (!res.ok) throw new Error('Failed to fetch quote')
      return res.json()
    },
  })

  const { data: versionsData } = useQuery({
    queryKey: ['contractor-quote-versions', tenantSlug, quoteNumber],
    queryFn: async () => {
      const res = await api.contractor.quotes[':id'].versions.$get({
        param: { id: quoteNumber },
        query: officeQuery,
      })
      if (!res.ok) throw new Error('Failed to fetch versions')
      return res.json()
    },
    enabled: !!quote,
  })

  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await api.contractor.quotes[':id'].approve.$post({
        param: { id: quoteNumber },
        query: officeQuery,
      })
      if (!res.ok) throw new Error('Failed to approve quote')
      return res.json()
    },
    onSuccess: () => {
      toast.success(t('detail.toast.approved'))
      queryClient.invalidateQueries({ queryKey: ['contractor-quote', tenantSlug, quoteNumber] })
      queryClient.invalidateQueries({ queryKey: ['contractor-quotes'] })
    },
    onError: () => {
      toast.error(t('detail.toast.approveFailed'))
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      const res = await api.contractor.quotes[':id'].reject.$post({
        param: { id: quoteNumber },
        query: officeQuery,
      })
      if (!res.ok) throw new Error('Failed to reject quote')
      return res.json()
    },
    onSuccess: () => {
      toast.success(t('detail.toast.rejected'))
      queryClient.invalidateQueries({ queryKey: ['contractor-quote', tenantSlug, quoteNumber] })
      queryClient.invalidateQueries({ queryKey: ['contractor-quotes'] })
    },
    onError: () => {
      toast.error(t('detail.toast.rejectFailed'))
    },
  })

  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [pdfError, setPdfError] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    const token = getSessionToken()
    const url = `${config.backendURL}/contractor/quotes/${encodeURIComponent(
      quoteNumber,
    )}/pdf?office=${encodeURIComponent(tenantSlug)}`
    fetch(url, {
      headers: { Authorization: `${AUTHORIZATION_HEADER_PREFIX}${token}` },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch PDF')
        return res.blob()
      })
      .then((blob) => {
        setPdfBlob(blob)
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === 'AbortError') return
        setPdfError(true)
      })

    return () => {
      controller.abort()
    }
  }, [quoteNumber])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!quote) {
    return (
      <div className="space-y-4">
        <Button asChild variant="outline" size="sm">
          <Link to="/$tenantSlug/quotes" params={{ tenantSlug }}>
            <ArrowLeftIcon className="size-4" />
            {t('detail.back')}
          </Link>
        </Button>
        <p className="text-muted-foreground">{t('detail.notFound')}</p>
      </div>
    )
  }

  const isPending = quote.status === 'sent' && !quote.approbationState
  const isMutating = approveMutation.isPending || rejectMutation.isPending
  const versions = versionsData?.versions
  const hasMultipleVersions = versions && versions.length > 1
  const salespersonName = displayNameOrNull(
    quote.salesperson && {
      firstName: quote.salesperson.givenName,
      lastName: quote.salesperson.familyName,
    },
  )
  const decidedBy = [
    displayNameOrNull(
      quote.approbationBy && {
        firstName: quote.approbationBy.givenName,
        lastName: quote.approbationBy.familyName,
      },
    ),
    quote.approbationAt ? formatUsDate(quote.approbationAt) : null,
  ]
    .filter(Boolean)
    .join(' · ')
  const formatAmount = (value: unknown) =>
    formatCurrency(value, { locale: toIntlLocale(i18n.language) })

  return (
    <div className="space-y-8">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <Button asChild variant="outline" size="icon">
            <Link to="/$tenantSlug/quotes" params={{ tenantSlug }}>
              <ArrowLeftIcon className="size-4" />
            </Link>
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold break-words">
              {quote.jobName ?? t('detail.untitled')}
            </h1>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono text-xs text-muted-foreground">{quote.quoteNumber}</span>
              {salespersonName && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground">{salespersonName}</span>
                </>
              )}
              <span className="text-muted-foreground">·</span>
              <QuoteVersionNav
                currentQuoteNumber={quoteNumber}
                currentVersion={quote.version}
                getStatusLabel={(status) =>
                  t(versionStatusKey(status), { defaultValue: capitalize(status ?? '') })
                }
                versions={versions}
                hasMultipleVersions={hasMultipleVersions}
                onNavigate={(versionQuoteNumber) =>
                  navigate({
                    to: '/$tenantSlug/quotes/$quoteNumber',
                    params: { tenantSlug, quoteNumber: versionQuoteNumber },
                  })
                }
              />
            </div>
          </div>
        </div>
      </header>

      {/* Two-column layout: PDF left, summary right */}
      <div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-8">
        {/* Left: PDF viewer */}
        <div className="min-w-0 flex-1">
          {pdfBlob ? (
            <PdfViewer
              file={pdfBlob}
              filename={getQuotePdfFilename({ slug: tenantSlug, quoteNumber })}
              className="h-[80vh]"
            />
          ) : pdfError ? (
            <div className="flex h-[80vh] flex-col items-center justify-center gap-2 rounded-md border">
              <p className="text-sm text-muted-foreground">{t('detail.pdfFailed')}</p>
            </div>
          ) : (
            <div className="flex h-[80vh] items-center justify-center rounded-md border">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>

        {/* Right: sticky summary + approve/reject */}
        <div className="w-full space-y-4 lg:sticky lg:top-6 lg:w-[328px] lg:shrink-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t('detail.summary')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Quote info */}
              <div className="space-y-2">
                {quote.jobName && (
                  <div className="flex items-start justify-between gap-2">
                    <span className="shrink-0 text-sm text-muted-foreground">
                      {t('detail.job')}
                    </span>
                    <span className="min-w-0 text-right text-sm font-medium break-words">
                      {quote.jobName}
                    </span>
                  </div>
                )}
                {quote.jobLocationAddress && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{t('detail.location')}</span>
                    <span className="max-w-[180px] text-right text-sm">
                      {quote.jobLocationAddress}
                    </span>
                  </div>
                )}
              </div>

              {/* Salesperson contact */}
              {salespersonName && (
                <>
                  <Separator />
                  <div className="flex items-center gap-2">
                    <UserIcon className="size-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{salespersonName}</p>
                      <p className="text-xs text-muted-foreground">{t('detail.yourContact')}</p>
                    </div>
                  </div>
                </>
              )}

              <Separator />

              {/* Totals */}
              <div className="rounded-lg bg-muted px-4 py-3">
                <div className="flex items-center justify-between py-1">
                  <span className="text-sm text-muted-foreground">{t('detail.subtotal')}</span>
                  <span className="text-sm text-muted-foreground">
                    {formatAmount(quote.calculated.subtotal)}
                  </span>
                </div>
                {Number(quote.calculated.taxAmount) > 0 && (
                  <div className="flex items-center justify-between py-1">
                    <span className="text-sm text-muted-foreground">{t('detail.tax')}</span>
                    <span className="text-sm text-muted-foreground">
                      {formatAmount(quote.calculated.taxAmount)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between pt-2">
                  <span className="text-base font-semibold">{t('detail.total')}</span>
                  <span className="text-base font-semibold">
                    {formatAmount(quote.calculated.total)}
                  </span>
                </div>
              </div>

              {/* Approve / Reject */}
              {isPending && (
                <div className="flex gap-2">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="flex-1" isDisabled={isMutating}>
                        <CheckCircleIcon className="size-4" />
                        {t('detail.approve')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('detail.approveDialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('detail.approveDialog.description')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => approveMutation.mutate()}>
                          {t('detail.approve')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" className="flex-1" isDisabled={isMutating}>
                        <XCircleIcon className="size-4" />
                        {t('detail.reject')}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('detail.rejectDialog.title')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('detail.rejectDialog.description')}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-white hover:bg-destructive/90"
                          onClick={() => rejectMutation.mutate()}
                        >
                          {t('detail.reject')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              )}

              {/* Already decided status */}
              {quote.approbationState === 'approved' && (
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                  <CheckCircleIcon className="size-4 text-green-600" />
                  <div className="text-sm">
                    <p>{t('status.approved')}</p>
                    <p className="text-xs text-muted-foreground">{decidedBy}</p>
                  </div>
                </div>
              )}
              {quote.approbationState === 'rejected' && (
                <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-2">
                  <XCircleIcon className="size-4 text-red-600" />
                  <div className="text-sm">
                    <p>{t('status.rejected')}</p>
                    <p className="text-xs text-muted-foreground">{decidedBy}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

function QuoteVersionNav({
  currentQuoteNumber,
  currentVersion,
  getStatusLabel,
  versions,
  hasMultipleVersions,
  onNavigate,
}: {
  currentQuoteNumber: string
  currentVersion: number
  getStatusLabel: (status: string | null) => string
  versions:
    | {
        id: string
        quoteNumber: string
        version: number
        status: string | null
        approbationState: string | null
      }[]
    | undefined
  hasMultipleVersions: boolean | undefined
  onNavigate: (quoteNumber: string) => void
}) {
  const { t } = useTranslation('quotes')

  if (!hasMultipleVersions) {
    return (
      <Button variant="outline" size="sm" className="h-6 gap-1.5 px-2 text-xs" isDisabled>
        {t('detail.version', { version: currentVersion })}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-6 gap-1.5 px-2 text-xs">
          {t('detail.version', { version: currentVersion })}
          <ChevronDownIcon className="size-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {versions!.map((v) => {
          const isCurrent = v.quoteNumber === currentQuoteNumber
          const effectiveStatus = v.approbationState ?? v.status
          return (
            <DropdownMenuItem
              key={v.id}
              onClick={() => {
                if (!isCurrent) onNavigate(v.quoteNumber)
              }}
            >
              <CircleDotIcon className={`size-3.5 ${getStatusDotClass(effectiveStatus)}`} />
              <span className="flex-1">
                {isCurrent
                  ? t('detail.versionCurrent', { version: v.version })
                  : t('detail.version', { version: v.version })}
              </span>
              <span className="text-xs text-muted-foreground">
                {getStatusLabel(effectiveStatus)}
              </span>
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
